import {
  getAttackConfiguration,
  getCommand,
  getServiceIdFromAttack,
  getTargetType
} from 'components/attacks/utils/attack-configuration'
import { getStateOfNodeByType } from 'components/scenarios/utils/graph-node-lifecycle'
import {
  getExternalActionTargetPrefix,
  getExternalActionText
} from 'components/scenarios/utils/graph/messaging'
import {
  isInfraAttackNode,
  isContinuousStatusCheckNode,
  isFailureFlagNode,
  isExternalActionNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { walkGraphUntilCondition } from 'components/scenarios/utils/graph/walkers'
import {
  ScenarioGraphNodeType,
  type GraphNode,
  type ScenarioGraph
} from 'types/api/scenarios/scenario'
import { capitalize, cleanJoin } from 'utils/strings'

/**
 * Parses failMessageResponse response id and attempts to find the node in the graph.
 * Health checks are special cases, there's a 'haltSource' that comes with the
 * response for health checks. This works for IHCs and non-IHCs.
 * It aggressively falls back to the returned BE message.
 * If the id isn't found in the graph (maybe the format of the returned string changed).
 *
 * The data structure:
 *```
 * {
 *  // details
 *  message?: string;
 *  // the specific HC or attack type
 *  name?: string;
 *  // HC, Kubernetes/Host/Container {Service}, FF
 *  type?: string;
 * }
 *```
 * Note, there's a future effort to change the BE response format. At that point,
 * this function will be removed. See EN-9003 for more details.
 */
export function getFailedNodeDetails(
  graph: ScenarioGraph,
  failMessageResponse: string | undefined,
  haltSource: string | undefined
): { message?: string; name?: string; type?: string } | undefined {
  if (!failMessageResponse) return

  /* Parse the id from predictable strings in the response */
  const [_, responseTail] = failMessageResponse.split('Halted by node: ')
  const [id, ...failReasonResponse] = responseTail?.split(': ') ?? []

  let failedNode: GraphNode | undefined

  if (
    /* Top-level health checks (observers) */
    haltSource === ScenarioGraphNodeType.ContinuousStatusCheck &&
    id &&
    graph.observers
  ) {
    failedNode =
      graph.observers[id] ??
      graph.observers.find((observer) => observer.id === id)
  } else if (
    /* Top-level external actions (external steps) */
    haltSource === ScenarioGraphNodeType.ExternalAction &&
    id &&
    graph.external_actions
  ) {
    failedNode =
      graph.external_actions[id] ??
      graph.external_actions.find((observer) => observer.id === id)
  } else {
    /* All other node types */
    walkGraphUntilCondition(graph, (n) => {
      const { isFailed, isHalted, isNotStarted } = getStateOfNodeByType(n)
      const isNodeInFailedState = isFailed || isHalted || isNotStarted

      if (isNodeInFailedState) {
        failedNode = n
      }

      return !isNodeInFailedState
    })
  }

  /* Fallback: returned in all unsuccessful parsing / finding cases */
  const fallback = { message: failMessageResponse }

  if (!failedNode) return fallback

  /* If the failed reason is on the node, use that.
   * Otherwise, get it from the original message */
  const message =
    failedNode.state?.failed_reason ?? failReasonResponse.join(': ')

  /* Make sure there's an id and that it matches */
  if (!id || (failedNode.id !== id && failedNode.guid !== id)) {
    return fallback
  }

  if (isContinuousStatusCheckNode(failedNode)) {
    return {
      message,
      name: failedNode.name,
      type: 'Health Check'
    }
  }

  if (isFailureFlagNode(failedNode)) {
    return {
      message,
      name: cleanJoin([
        failedNode.failureFlagInputForScenario.app.name,
        failedNode.failureFlagInputForScenario.flag.name &&
          `(${failedNode.failureFlagInputForScenario.flag.name})`
      ]),
      type: 'Failure Flag Impact'
    }
  }

  if (isExternalActionNode(failedNode)) {
    const { type } = failedNode.externalActionRequest
    const externalActionText = getExternalActionText(type)
    const externalActionTargetPrefix = getExternalActionTargetPrefix(
      type,
      false
    )

    if (externalActionText && externalActionTargetPrefix) {
      return {
        message,
        name: `${externalActionText} ${externalActionTargetPrefix}:`,
        type: 'External Action'
      }
    }

    return fallback
  }

  /* There isn't much other context to provide in this case */
  if (!isInfraAttackNode(failedNode)) return fallback

  const attackConfig = getAttackConfiguration(failedNode)

  if (!attackConfig) return fallback

  const command = getCommand(attackConfig)
  const targetType = getTargetType(attackConfig)
  const serviceId = getServiceIdFromAttack(failedNode)

  return {
    message,
    /* Attacks don't have names, so use the command as the name */
    name: capitalize(command.infraCommandType),
    type: cleanJoin([targetType, serviceId && 'Service', 'Experiment'])
  }
}
